// One post, opened from Home: the same three numbers with its rank among the month's posts, then
// one card per channel in that channel's own words, a link to open it there, and Make one like this.
import type { PostResultsDto } from '@str/api/types';
import { shapeLabel, type Channel } from '@str/shared';
import { Link, useNavigate, useParams } from 'react-router';
import { CHANNEL_NAMES, REPORT_WINDOW_DAYS, prefillFromPost, whenParts } from '@/lib/home';
import { useActiveProperty } from '@/lib/property-store';
import { ApiError, errorMessage } from '@/lib/api';
import { JOB_STATUS, publishFailure } from '@/lib/publish-status';
import { useChannels, usePostResults } from '@/lib/queries';
import { resultLink } from '@/lib/result-link';
import { mediaStyle } from '@/lib/scenes';

type Metrics = NonNullable<PostResultsDto['channels'][number]['metrics']>;
/** Each channel's own words, in its own order. */
const WORDS: Record<Channel, [keyof Metrics, string][]> = {
  IG: [
    ['reach', 'reached'],
    ['views', 'plays'],
    ['likes', 'likes'],
    ['comments', 'comments'],
    ['saves', 'saves'],
  ],
  FB: [
    ['reach', 'reached'],
    ['views', 'plays'],
    ['reactions', 'reactions'],
    ['comments', 'comments'],
    ['shares', 'shares'],
  ],
  TT: [
    ['views', 'views'],
    ['likes', 'likes'],
    ['comments', 'comments'],
    ['shares', 'shares'],
  ],
  YT: [
    ['views', 'views'],
    ['likes', 'likes'],
    ['comments', 'comments'],
  ],
};
const n = (v: number) => v.toLocaleString('en-US');

function ago(iso: string | null): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
}

/** Out longer than the channels take to report: "still counting" would no longer be true. */
function pastReportWindow(iso: string | null): boolean {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() >= REPORT_WINDOW_DAYS * 86_400_000;
}

export function PostResults() {
  const { id = '' } = useParams();
  const { active } = useActiveProperty();
  const navigate = useNavigate();
  const q = usePostResults(id);
  const data = q.data;
  // Which providers publish for real: a mock one's permalinks lead nowhere.
  const providers = useChannels(active.id).data?.providers ?? [];
  const modeOf = (provider: string) => providers.find((p) => p.key === provider)?.mode;

  const head = (title: string) => (
    <div className="h-head">
      <span className="h-caps">
        <Link to="/app">← Home</Link>
        <span> · {active.name} · Post results</span>
      </span>
      <Link to="/app/create" className="btn btn-primary hv">
        Make a post
      </Link>
      <span className="sr-only">{title}</span>
    </div>
  );

  if (q.isError) {
    // A post that is not the host's reads exactly like one that does not exist.
    const gone = q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 403);
    return (
      <div className="screen h-page">
        {head(gone ? 'Post not found' : 'Post did not load')}
        <div className="h-empty" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {gone ? (
            <>
              <strong>This post isn&apos;t here.</strong>
              <span>
                It may have been removed, or the link is wrong. <Link to="/app">Back to Home</Link>{' '}
                or <Link to="/app/posts">see your posts</Link>.
              </span>
            </>
          ) : (
            <>
              <strong>Couldn&apos;t load this post.</strong>
              <span>{errorMessage(q.error)}</span>
              <button
                type="button"
                className="btn-pill hv"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => void q.refetch()}
              >
                Try again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!data)
    return (
      <div className="screen h-page">
        {head('')}
        <div role="status" aria-label="Loading this post" className="h-results">
          <div className="h-results-post">
            <span className="sk" style={{ width: '100%', aspectRatio: '4 / 5' }} />
            <span className="sk" style={{ width: 240, height: 20 }} />
            <span className="sk" style={{ width: 300, height: 12 }} />
          </div>
          <div className="h-results-side">
            <div className="h-tiles three">
              {[0, 1, 2].map((i) => (
                <span key={i} className="sk" style={{ height: 96 }} />
              ))}
            </div>
            {[0, 1].map((i) => (
              <span key={i} className="sk" style={{ height: 120 }} />
            ))}
          </div>
        </div>
      </div>
    );

  const { post, tiles } = data;
  const when = post.publishedAt
    ? whenParts(post.publishedAt, post.scheduledTime, post.timezone)
    : null;
  const shape = shapeLabel(post.shapeId);
  const made = post.mediaMode === 'imagine' ? 'Imagined' : 'Made from your photos';
  // Until a channel has reported, the numbers are not in: a dash, never a zero ranked first.
  // Only the channels that took the post count here: a refused one never reports.
  const live = data.channels.filter((c) => c.status === 'published');
  const awaiting = live.length > 0 && live.every((c) => !c.metrics);
  const tileRows: [keyof typeof tiles, string][] = [
    ['reached', 'people reached, all channels'],
    ['engaged', 'likes, comments, saves and shares'],
    ['clicks', 'clicks to your link'],
  ];

  return (
    <div className="screen h-page">
      {head(post.title)}
      <div className="h-results h-rise">
        <div className="h-results-post">
          <div className="h-results-pic" style={mediaStyle({ url: post.imageUrl, scene: 'dusk' })}>
            <span className="tag tag-sm">{post.format}</span>
          </div>
          <h1 className="h-h1 small">{post.title}</h1>
          {when && (
            <p className="h-sub">
              Went out {when.day} · {when.time} ·{' '}
              {post.channels.map((c) => CHANNEL_NAMES[c]).join(', ')}
            </p>
          )}
          <p className="h-sub">
            Shape: {shape ?? (post.format === 'Reel' ? 'a moment' : 'your own words')} · {made}
          </p>
          <button
            type="button"
            className="btn btn-primary hv"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              prefillFromPost(active.id, post);
              if (post.format === 'Reel')
                void navigate('/app/reel', { state: { fresh: crypto.randomUUID(), follow: true } });
              else void navigate('/app/create', { state: { follow: true } });
            }}
          >
            Make one like this
          </button>
        </div>
        <div className="h-results-side">
          <div className="h-tiles three">
            {tileRows.map(([key, label]) => {
              const t = tiles[key];
              return (
                <div key={key} role="group" aria-label={label} className="h-tile compact">
                  <span className="h-tile-n">
                    {awaiting && key !== 'clicks' ? '—' : n(t.value)}
                  </span>
                  <span className="h-tile-l">
                    {awaiting && key !== 'clicks' ? `${label} · not in yet` : label}
                  </span>
                  {t.rank !== null && !(awaiting && key !== 'clicks') && (
                    <span className="h-tile-d">
                      <span className="h-up">
                        <span aria-hidden="true">▲ </span>
                        <span>{`#${t.rank} of ${t.of}`}</span>
                      </span>{' '}
                      <span>{t.monthLabel}</span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {data.channels.map((c) => {
            const name = CHANNEL_NAMES[c.channel];
            if (c.status !== 'published') {
              // Sent but not taken: the same status and reason the Posts list gives.
              const s = JOB_STATUS[c.status];
              return (
                <section key={c.channel} role="region" aria-label={name} className="h-card">
                  <div className="h-card-head">
                    <span>
                      <b>{name}</b>
                      <span className="muted">
                        {' '}
                        · {c.channel === 'TT' || c.channel === 'YT' ? 'Video' : post.format}
                      </span>
                    </span>
                    <span style={{ color: s.color, fontWeight: 600 }}>{s.label}</span>
                  </div>
                  <p className="h-sub">
                    {c.status === 'failed' || c.status === 'unconfirmed'
                      ? publishFailure(c.error, [c.channel])
                      : c.status === 'skipped'
                        ? `${name} wasn’t connected, so it didn’t go out there.`
                        : `Still going out on ${name}.`}
                  </p>
                </section>
              );
            }
            return (
              <section key={c.channel} role="region" aria-label={name} className="h-card">
                <div className="h-card-head">
                  <span>
                    <b>{name}</b>
                    <span className="muted">
                      {' '}
                      · {c.channel === 'TT' || c.channel === 'YT' ? 'Video' : post.format} ·{' '}
                      {ago(c.publishedAt)}
                    </span>
                  </span>
                  {(() => {
                    const link = resultLink(c.permalink, modeOf(c.provider));
                    if (!link) return null;
                    if (link.kind === 'simulated')
                      return <span className="muted">Simulated post (preview)</span>;
                    return (
                      <a href={link.href} target="_blank" rel="noreferrer">
                        Open on {name}
                      </a>
                    );
                  })()}
                </div>
                {c.metrics ? (
                  <div className="h-words">
                    {WORDS[c.channel]
                      .filter(([k]) => !(c.channel === 'FB' && k === 'views' && !c.metrics![k]))
                      .map(([k, word]) => (
                        <span key={k} className="h-word">
                          <b>{n(c.metrics![k])}</b>
                          <span>{word}</span>
                        </span>
                      ))}
                  </div>
                ) : (
                  <p className="h-sub">
                    {c.pending
                      ? `${c.pending}.`
                      : pastReportWindow(c.publishedAt)
                        ? `Not available yet: ${name} has not reported numbers for this post.`
                        : `Still counting. Channels report back within ${REPORT_WINDOW_DAYS} days.`}
                  </p>
                )}
                <p className="h-sub">
                  {c.clicks === null
                    ? `${name} gives no link clicks.`
                    : `${c.clicks} ${c.clicks === 1 ? 'click' : 'clicks'} on your link`}
                </p>
              </section>
            );
          })}
          {data.clicksUnplaced > 0 && (
            <p className="h-note">
              {data.clicksUnplaced} more {data.clicksUnplaced === 1 ? 'click' : 'clicks'} came
              without saying from where.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
